import { Tooltip } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";
import { skipToken } from "@reduxjs/toolkit/query";
import {
	CoreButton,
	CoreMenuButton,
	CoreMenuItem,
	CoreNoResults,
	CoreSelectTextField,
	CoreSwitch,
	CoreSwitchSkeleton,
	CoreTextFieldSkeleton,
} from "@verity/components/core";
import { memo, useContext, useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CompositeTable, {
	CompositeTableBodyRow,
	CompositeTableCell,
	CompositeTableHeaderRow,
	CompositeTablePaginationWrapper,
	CompositeTableSkeleton,
} from "../../../components/composite-table";
import apiClient from "../../../helpers/api-client";
import { getProduct } from "../../../helpers/url";
import Add from "../../../images/add.svg?react";
import MoreVert from "../../../images/portal/more-vert.svg?react";
import MfaIcon from "../../../images/portal/two-factor-authentication.svg?react";
import TextSkeleton from "../../../utility/TextSkeleton";
import { PortalControllerContext } from "../../context";
import CreateUserModal from "./create-user-modal";
import { MemoizedRosterSearchBar } from "./roster-search-bar";
import styles from "./roster-table.module.css";
import {
	Lti1p3Account,
	PrivilegeGroup,
	ROLE_CATEGORIES,
	RoleCategory,
	RosterSortConfig,
	RosterUser,
} from "./roster-types";
import {
	RosterUsersQueryArgs,
	useGetPrivilegeGroupsQuery,
	useGetUserAccountsQuery,
	useGetUsersQuery,
	useUpdateAccountMutation,
	useUpdateUserMutation,
} from "./store/roster-api";
import { useAppDispatch, useAppSelector } from "./store/roster-hooks";
import { setCurrentPage, setSortConfigs } from "./store/roster-slice";

const ROLE_PRIVILEGE_GROUP_NAME_PREFIXES: Record<RoleCategory, string[]> = {
	ADMIN: ["Lti-Institute-Admin"],
	INSTRUCTOR: ["Lti-Institute-Instructor", "Lti-Course-Admin", "Lti-Course-Instructor"],
	STUDENT: ["Lti-Institute-Student", "Lti-Course-Student"],
};

const constructPrivilegeGroupNameRegexFromRoles = (roles: RoleCategory[]) =>
	roles.length > 0 ? `^(${roles.flatMap((role) => ROLE_PRIVILEGE_GROUP_NAME_PREFIXES[role]).join("|")}).*` : undefined;

const findMostPrivilegedRole = (roles: RoleCategory[]) => {
	const mostPrivilegedRoleIndex = roles
		.map((category) => ROLE_CATEGORIES.indexOf(category))
		.reduce((currValue, categoryIndex) => Math.min(currValue, categoryIndex), ROLE_CATEGORIES.length - 1);
	return ROLE_CATEGORIES[mostPrivilegedRoleIndex];
};

const categorizePrivilegeGroup = ({ name }: PrivilegeGroup): RoleCategory =>
	(Object.entries(ROLE_PRIVILEGE_GROUP_NAME_PREFIXES).find(([, prefixes]) =>
		prefixes.some((prefix) => name.startsWith(prefix)),
	)?.[0] as RoleCategory | undefined) ?? "STUDENT";

type RosterRowProps = {
	usersUpdating: boolean;
	user: RosterUser;
	mfaRequired: boolean;
};

const RosterRow = ({ usersUpdating, user, mfaRequired }: RosterRowProps) => {
	const { id, name, privilegeGroupIds } = user;
	const { portalContext } = useContext(PortalControllerContext);
	const { lms, instituteUuid } = portalContext;

	const [actionMenuShowing, setActionMenuShowing] = useState(false);

	const [updateUser] = useUpdateUserMutation();
	const [updateAccount] = useUpdateAccountMutation();
	const {
		data: accounts,
		isLoading: accountsLoading,
		isFetching: accountsUpdating,
		refetch: refetchAccounts,
	} = useGetUserAccountsQuery(user.id);
	const {
		data: privilegeGroups,
		isLoading: privilegeGroupsLoading,
		isFetching: privilegeGroupsUpdating,
	} = useGetPrivilegeGroupsQuery({
		userId: user.id,
		privilegeGroupIds: privilegeGroupIds,
	});
	const maximumRole = findMostPrivilegedRole(privilegeGroups?.map(categorizePrivilegeGroup) ?? []);
	const hasPrivilegeMappableAccounts = !!accounts?.some(({ type }) => type === "lti" || type === "saml");
	const locked =
		hasPrivilegeMappableAccounts &&
		(accounts?.every((account) => {
			switch (account.type) {
				case "lti":
					return account.nrpsPrivilegeGroupMappingEnabled;
				case "saml":
					return !account.samlPrivilegeGroupMappingEnabled;
				default:
					return false;
			}
		}) ??
			false);

	const { t } = useTranslation();

	const visibleRoleCategories: RoleCategory[] =
		lms || maximumRole === "STUDENT"
			? [...ROLE_CATEGORIES]
			: ROLE_CATEGORIES.filter((category) => category !== "STUDENT");
	const invalidText = t("portal.components.roster.table.row.invalid");

	const product: "candidate" | "verity" = getProduct() === "candidate" ? "candidate" : "verity";
	const mfaEnabled = mfaRequired && !!accounts?.some((account) => account.twoFactorVerified);

	return (
		<CompositeTableBodyRow>
			{product !== "candidate" && (
				<CompositeTableCell>
					{accountsLoading ? (
						<TextSkeleton width={50} />
					) : (
						accounts?.find(
							(account): account is Lti1p3Account => account.type === "lti" && !!account.secondaryLmsUserId,
						)?.secondaryLmsUserId || invalidText
					)}
				</CompositeTableCell>
			)}
			<CompositeTableCell>
				{accountsLoading ? <TextSkeleton width={150} /> : accounts?.find(({ email }) => email)?.email || invalidText}
				{!accountsLoading && mfaEnabled && (
					<Tooltip title={t("portal.components.roster.table.row.mfaTooltip.enabled")}>
						<span className={styles.mfaChip}>
							<MfaIcon />
						</span>
					</Tooltip>
				)}
			</CompositeTableCell>
			<CompositeTableCell>{name || invalidText}</CompositeTableCell>
			<CompositeTableCell>
				{privilegeGroupsLoading ? (
					<CoreTextFieldSkeleton className={styles.roleSelect} size="medium" />
				) : (
					<CoreSelectTextField<RoleCategory>
						className={styles.roleSelect}
						value={maximumRole}
						onChange={async (e) => {
							const newRole = e.target.value as RoleCategory;
							if (instituteUuid && visibleRoleCategories.includes(newRole)) {
								try {
									const newPrivilegeGroups = await apiClient.roster.getPrivilegeGroupIdsForRole(instituteUuid, newRole);
									await updateUser({ id, privilegeGroupIds: newPrivilegeGroups }).unwrap();
								} catch (e) {
									console.error(e);
								}
							} else {
								console.error("no instituteUuid");
							}
						}}
						disabled={usersUpdating || privilegeGroupsUpdating}
						label={t("portal.components.roster.table.headers.role")}
						renderValue={(value) => <span>{t(`portal.components.roster.table.row.role.${product}.${value}`)}</span>}
					>
						{visibleRoleCategories.map((role) => (
							<CoreMenuItem key={role} value={role}>
								{t(`portal.components.roster.table.row.role.${product}.${role}`)}
							</CoreMenuItem>
						))}
					</CoreSelectTextField>
				)}
			</CompositeTableCell>
			<CompositeTableCell>
				<div className={styles.lockedStatusContainer}>
					{accountsLoading ? (
						<CoreSwitchSkeleton labelTextType="left" />
					) : (
						<CoreSwitch
							checked={locked}
							onChange={async (e) => {
								if (instituteUuid) {
									if (accounts) {
										const newLocked = e.target.checked;
										try {
											await Promise.all(
												accounts.map(async (account) => {
													switch (account.type) {
														case "lti":
															await updateAccount({
																instituteUuid,
																userId: id,
																accountId: account.id,
																nrpsPrivilegeGroupMappingEnabled: newLocked,
															}).unwrap();
															break;
														case "saml":
															await updateAccount({
																instituteUuid,
																userId: id,
																accountId: account.id,
																samlPrivilegeGroupMappingEnabled: !newLocked,
															}).unwrap();
															break;
														default:
															console.error(`cannot update locked status of ${account.type} account`);
													}
												}),
											);
										} catch (e) {
											console.error(e);
										}
									}
								} else {
									console.error("no instituteUuid");
								}
							}}
							disabled={!accounts || accountsUpdating || !hasPrivilegeMappableAccounts}
							disabledTooltip={
								!hasPrivilegeMappableAccounts ? t("portal.components.roster.table.row.notLtiSaml") : undefined
							}
							labelTextType="left"
							labelOnText={t("portal.components.roster.table.row.locked")}
							labelOffText={t("portal.components.roster.table.row.unlocked")}
						/>
					)}
				</div>
			</CompositeTableCell>
			{mfaRequired && (
				<CompositeTableCell>
					<div className={styles.actionsCellContainer}>
						<CoreMenuButton
							menuShowing={actionMenuShowing}
							setMenuShowing={setActionMenuShowing}
							aria-label={t("portal.components.roster.table.headers.actions")}
							icon={<MoreVert />}
						>
							<Tooltip title={!mfaEnabled ? t("portal.components.roster.table.row.mfaTooltip.disabled") : ""}>
								<CoreMenuItem
									disabled={!mfaEnabled}
									onClick={async () => {
										if (accounts) {
											const accountId = accounts.find(({ email }) => email)?.id;
											if (accountId) {
												try {
													const accessToken = await apiClient.getAccessToken();
													if (accessToken) {
														await apiClient.basicAuth.disableTwoFactor({ accountId, accessToken });
														void refetchAccounts();
													}
												} catch (e) {
													console.error(e);
												}
											}
										}
										setActionMenuShowing(false);
									}}
								>
									{t("portal.components.roster.table.row.actions.resetMFA")}
								</CoreMenuItem>
							</Tooltip>
						</CoreMenuButton>
					</div>
				</CompositeTableCell>
			)}
		</CompositeTableBodyRow>
	);
};

const RosterTable = () => {
	const { portalContext } = useContext(PortalControllerContext);
	const { instituteUuid } = portalContext;

	const dispatch = useAppDispatch();
	const { currentPage, pageSize, filterQuery, searchQuery, sortConfigs } = useAppSelector((state) => state.roster);

	const queryArgs = useMemo((): RosterUsersQueryArgs | typeof skipToken => {
		if (!instituteUuid) {
			return skipToken;
		}

		return {
			instituteUuid,
			privilegeGroupName:
				filterQuery.role.length === 0
					? undefined
					: filterQuery.role.map((role) => constructPrivilegeGroupNameRegexFromRoles([role]) ?? ".*"),
			excludedPrivilegeGroupName:
				filterQuery.role.length === 0
					? undefined
					: filterQuery.role.map(
							(role) =>
								constructPrivilegeGroupNameRegexFromRoles(ROLE_CATEGORIES.slice(0, ROLE_CATEGORIES.indexOf(role))) ??
								"^$",
						),
			privilegesLocked: filterQuery.locked.length > 1 ? undefined : filterQuery.locked[0],
			searchQuery: searchQuery.idOrEmail,
			sortBy: sortConfigs.map(({ key }) => key),
			sortDirection: sortConfigs.map(({ direction }) => direction),
			page: currentPage,
			size: pageSize,
		};
	}, [instituteUuid, searchQuery, filterQuery, sortConfigs, currentPage, pageSize]);
	const { data, isLoading, isError, isFetching } = useGetUsersQuery(queryArgs, {
		refetchOnFocus: false,
		refetchOnMountOrArgChange: true,
	});

	const [createUserModalShowing, setCreateUserModalShowing] = useState(false);
	const [mfaRequired, setMfaRequired] = useState(false);

	useEffect(() => {
		if (!instituteUuid) {
			setMfaRequired(false);
			return;
		}

		void apiClient.institute
			.getAccessControlInfo(instituteUuid)
			.then((accessControlInfo) => {
				setMfaRequired(accessControlInfo.mfaRequired ?? false);
			})
			.catch(() => {
				setMfaRequired(false);
			});
	}, [instituteUuid]);

	const { t } = useTranslation();
	const tableId = useId();

	const columns = [
		...(getProduct() !== "candidate"
			? [
					{
						key: "lmsUserId" as const,
						content: t("portal.components.roster.table.headers.lmsUserId"),
						width: 200,
					},
				]
			: []),
		{
			key: "email" as const,
			content: t("portal.components.roster.table.headers.email"),
			sortable: true,
			width: "50%",
		},
		{
			key: "name" as const,
			content: t("portal.components.roster.table.headers.name"),
			sortable: true,
			width: "50%",
		},
		{
			key: "role" as const,
			content: t("portal.components.roster.table.headers.role"),
			width: 200,
		},
		{
			key: "privilegesLocked" as const,
			content: t("portal.components.roster.table.headers.locked"),
			sortable: true,
			width: 200,
		},
		...(mfaRequired
			? [
					{
						key: "actions" as const,
						content: t("portal.components.roster.table.headers.actions"),
						width: 150,
					},
				]
			: []),
	];

	const isLoaded = !isLoading && !isError;

	return (
		<>
			<MemoizedRosterSearchBar ariaControls={tableId} totalResults={data?.totalElements ?? 0}>
				<CoreButton
					onClick={() => setCreateUserModalShowing(true)}
					aria-haspopup="dialog"
					startIcon={
						<SvgIcon>
							<Add />
						</SvgIcon>
					}
				>
					{t("portal.components.roster.createUser.createUser")}
				</CoreButton>
			</MemoizedRosterSearchBar>
			<CompositeTablePaginationWrapper
				className={styles.tableContainer}
				page={currentPage}
				onChange={(newPage) => dispatch(setCurrentPage(newPage))}
				count={data?.totalPages ?? 0}
			>
				{isLoading && <CompositeTableSkeleton columns={columns} />}
				{isLoaded &&
					data &&
					(data.totalElements > 0 ? (
						<CompositeTable
							id={tableId}
							className={styles.table}
							head={
								<CompositeTableHeaderRow
									columns={columns}
									sortBy={sortConfigs}
									onSortByChange={(newSortConfigs: RosterSortConfig[]) => dispatch(setSortConfigs(newSortConfigs))}
								/>
							}
						>
							{data.users.map((user) => (
								<RosterRow key={user.id} usersUpdating={isFetching} user={user} mfaRequired={mfaRequired} />
							))}
						</CompositeTable>
					) : (
						<CoreNoResults className={styles.noResults} header={t("portal.components.roster.table.error.noResults")}>
							{t("portal.components.roster.table.error.noResultsDescription")}
						</CoreNoResults>
					))}
				{isError && (
					<CoreNoResults
						className={styles.noResults}
						variant="failed"
						header={t("portal.components.roster.table.error.errorLoading")}
					>
						{t("portal.components.roster.table.error.errorLoadingDescription")}
					</CoreNoResults>
				)}
			</CompositeTablePaginationWrapper>
			{createUserModalShowing && <CreateUserModal onClose={() => setCreateUserModalShowing(false)} />}
		</>
	);
};

export const MemoizedRosterTable = memo(RosterTable);
